import { Alert, Flex } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { sendFeedback } from '@/api/interviews';
import FeedbackForm from '@/components/form/FeedbackForm';
import { COMPLETED } from '@/constants/statuses';
import { useApiMutation } from '@/hooks/api';
import { useFeedbackQuery } from '@/hooks/queries/useFeedbackQuery';
import { useUrlState } from '@/hooks/useUrlState';
import { queryKeys } from '@/shared/lib/queryKeys';
import { getRandomColor } from '@/theme/avatarPalette';
import Avatar from '@/UI/Avatar/Avatar';
import { LabelTitle, PageTitle, SectionHeading } from '@/UI/typography';
import { formatLongDate, formatTime } from '@/utils/formatDate';

import type { FeedbackFormValues } from '@/components/form/FeedbackForm';
import type { InterviewRoom } from '@/types/interview';

interface GuestFormProps {
  interview: InterviewRoom;
  review?: string | null;
}

const GuestFeedbackForm = ({ interview, review }: GuestFormProps) => {
  const [expired, setExpired] = useState(false);
  const { t, i18n } = useTranslation();
  const { history } = useUrlState();
  const mainInterview = interview.current_stage?.tasks?.find(
    (task) => task.is_planning_task,
  );
  const clientFeedbackCompleted = interview.current_stage?.feedbacks?.find(
    (feedback) =>
      review
        ? feedback.feedback_type === 'recruiter_feedback'
        : feedback.feedback_type === 'client_feedback' && feedback.is_completed,
  );
  const completed = !!clientFeedbackCompleted;
  const { data: completedFeedback = null } = useFeedbackQuery(
    interview.id,
    clientFeedbackCompleted?.id,
    { enabled: completed && Boolean(clientFeedbackCompleted?.id) },
  );
  useEffect(() => {
    const params = new URLSearchParams(history.location.search);
    const stage = params.get('stage');
    const isTaskCompleted = !interview.current_stage?.tasks
      // .filter((task) => !task.is_feedback_task)
      ?.find((task) => task.status === COMPLETED);

    if (
      stage &&
      interview.current_stage?.stage_name?.toLowerCase() !== stage.toLowerCase()
    ) {
      setExpired(true);
    } else if (isTaskCompleted) {
      setExpired(true);
    }
  }, [interview, history]);

  const feedbackMutation = useApiMutation({
    mutationFn: (data: FeedbackFormValues) => sendFeedback(interview.id, data),
    successKey: 'FEEDBACK_SENT',
    invalidateKeys: [queryKeys.interviews.detail(interview.id)],
  });

  const onFinish = (data: FeedbackFormValues) => feedbackMutation.mutate(data);

  return (
    <div>
      <PageTitle>{t(interview.current_stage?.stage_name ?? '')}</PageTitle>
      <Flex align="center" justify="space-between">
        <Avatar
          name={interview.candidate.name}
          color={getRandomColor()}
          title={interview.candidate.name}
          description={interview.candidate.current_role}
        />
        <Flex align="center">
          {!expired && (
            <Flex vertical>
              <LabelTitle>
                {mainInterview && (
                  <>
                    {formatLongDate(mainInterview.time_from, i18n.language)},{' '}
                    {formatTime(mainInterview.time_from, i18n.language)} -{' '}
                    {formatTime(mainInterview.time_to, i18n.language)}
                  </>
                )}
              </LabelTitle>
              <LabelTitle type="secondary">
                {mainInterview && mainInterview?.place}
              </LabelTitle>
            </Flex>
          )}
        </Flex>
      </Flex>
      {expired ? (
        <Alert type="error" showIcon={false} title={t('NOT_ACTUAL')} />
      ) : (
        <>
          <SectionHeading>{t('FILL_IN')}</SectionHeading>

          <FeedbackForm
            onComplete={onFinish}
            completed={completed}
            isClient={true}
            hideMessage={true}
            data={completedFeedback}
            loading={feedbackMutation.loading}
          />
        </>
      )}
    </div>
  );
};

export default GuestFeedbackForm;
